import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormControl,
	FormLabel,
	FormErrorMessage,
	Input,
	RadioGroup,
	Radio,
	Stack,
	Text,
	Checkbox,
	CheckboxGroup,
	Divider,
	Box,
} from "@chakra-ui/react";
import { FiColumns, FiDollarSign, FiEdit3, FiGrid, FiHash, FiType } from "react-icons/fi";

import { Button } from "newui/components/Button";
import type { RfxQuoteColumnType } from "../../Submission/types/quote.type";

export interface ExistingValueColumnSource {
	quoteId: number;
	quoteName: string;
	valueColumns: string[];
}

export interface AddColumnModalProps {
	existingValueColumns?: ExistingValueColumnSource[];
	isOpen: boolean;
	isSubmitting?: boolean;
	onClose: () => void;
	onSubmit: (columnType: RfxQuoteColumnType, label: string) => void;
}

const COLUMN_TYPE_OPTIONS: {
	description: string;
	icon: React.ReactNode;
	label: string;
	value: RfxQuoteColumnType;
}[] = [
	{
		value: "description",
		label: "Description",
		description: "Additional text column defined by the client (read-only to supplier)",
		icon: <FiEdit3 className="text-blue-500" size={20} />,
	},
	{
		value: "response_text",
		label: "Response Text",
		description: "Text column for the supplier to provide additional information",
		icon: <FiType className="text-green-500" size={20} />,
	},
	{
		value: "response_number",
		label: "Response Number",
		description: "Numeric column for the supplier to enter a number value",
		icon: <FiHash className="text-teal-500" size={20} />,
	},
	{
		value: "response_quantity",
		label: "Response Quantity",
		description: "Quantity column for the supplier to enter quantities per row",
		icon: <FiGrid className="text-purple-500" size={20} />,
	},
	{
		value: "response_value",
		label: "Response Value",
		description: "Currency/value column — used for pricing and aggregated in summary tables",
		icon: <FiDollarSign className="text-amber-500" size={20} />,
	},
	{
		value: "total",
		label: "Total",
		description: "Auto-calculated column (quantity x sum of values). Requires quantity + value columns",
		icon: <FiColumns className="text-red-500" size={20} />,
	},
];

const AddColumnModal: React.FC<AddColumnModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	isSubmitting = false,
	existingValueColumns = [],
}) => {
	const [columnType, setColumnType] = useState<RfxQuoteColumnType>("description");
	const [label, setLabel] = useState("");
	const [labelError, setLabelError] = useState("");
	const [selectedExisting, setSelectedExisting] = useState<string[]>([]);

	const hasExistingValueColumns = existingValueColumns.some((src) => src.valueColumns.length > 0);

	useEffect(() => {
		if (isOpen) {
			setColumnType("description");
			setLabel("");
			setLabelError("");
			setSelectedExisting([]);
		}
	}, [isOpen]);

	const validateLabel = useCallback(
		(value: string): boolean => {
			if (!value.trim()) {
				setLabelError("Column label is required");
				return false;
			}
			setLabelError("");
			return true;
		},
		[]
	);

	// Cross-table collision warning for value columns
	const collisionWarning = useMemo((): string | null => {
		if (columnType !== "response_value" || !label.trim()) {
			return null;
		}
		const trimmedLower = label.trim().toLowerCase();
		const matchingSources = existingValueColumns.filter((src) =>
			src.valueColumns.some((col) => col.toLowerCase() === trimmedLower)
		);
		if (matchingSources.length === 0) {
			return null;
		}
		const tableNames = matchingSources.map((src) => src.quoteName).join(", ");
		return `This name is also used in ${tableNames}. Summary tables will aggregate them together.`;
	}, [columnType, label, existingValueColumns]);

	const handleSubmit = useCallback(() => {
		if (columnType === "response_value" && selectedExisting.length > 0) {
			selectedExisting.forEach((col) => {
				onSubmit("response_value", col);
			});
			onClose();
			return;
		}

		if (!validateLabel(label)) {
			return;
		}
		onSubmit(columnType, label.trim());
		onClose();
	}, [columnType, label, selectedExisting, onSubmit, onClose, validateLabel]);

	const handleAddAllFromTable = useCallback(
		(source: ExistingValueColumnSource) => {
			source.valueColumns.forEach((col) => {
				onSubmit("response_value", col);
			});
			onClose();
		},
		[onSubmit, onClose]
	);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent data-testid="add-column-modal">
				<ModalHeader>Add Column</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<FormControl className="mb-4">
						<FormLabel className="text-sm font-medium text-gray-700">
							Column Type
						</FormLabel>
						<RadioGroup
							value={columnType}
							onChange={(val) => setColumnType(val as RfxQuoteColumnType)}
						>
							<Stack spacing={3}>
								{COLUMN_TYPE_OPTIONS.map((opt) => (
									<Box
										key={opt.value}
										className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:bg-blue-50/30"
										data-testid={`column-type-${opt.value}`}
									>
										<Radio value={opt.value} className="mt-0.5" />
										<div className="flex items-start gap-2">
											{opt.icon}
											<div>
												<Text className="text-sm font-medium text-gray-900">
													{opt.label}
												</Text>
												<Text className="text-xs text-gray-500">
													{opt.description}
												</Text>
											</div>
										</div>
									</Box>
								))}
							</Stack>
						</RadioGroup>
					</FormControl>

					{columnType === "response_value" && hasExistingValueColumns && (
						<>
							<Divider className="my-4" />
							<Box className="mb-4">
								<Text className="mb-2 text-sm font-medium text-gray-700">
									Use existing value columns
								</Text>
								<Text className="mb-3 text-xs text-gray-500">
									Select value columns from other quote tables in this RFx, or add
									all from a table.
								</Text>
								{existingValueColumns
									.filter((src) => src.valueColumns.length > 0)
									.map((source) => (
										<Box
											key={source.quoteId}
											className="mb-3 rounded-lg border border-gray-200 p-3"
										>
											<div className="mb-2 flex items-center justify-between">
												<Text className="text-sm font-medium text-gray-700">
													{source.quoteName}
												</Text>
												<Button
													size="xs"
													btntype="secondary"
													onClick={() =>
														handleAddAllFromTable(source)
													}
													data-testid={`add-all-from-${source.quoteId}`}
												>
													Add all
												</Button>
											</div>
											<CheckboxGroup
												value={selectedExisting}
												onChange={(vals) =>
													setSelectedExisting(vals as string[])
												}
											>
												<Stack spacing={1}>
													{source.valueColumns.map((col) => (
														<Checkbox
															key={`${source.quoteId}-${col}`}
															value={col}
															data-testid={`existing-val-${col}`}
														>
															<Text className="text-sm">
																{col}
															</Text>
														</Checkbox>
													))}
												</Stack>
											</CheckboxGroup>
										</Box>
									))}
							</Box>
							<Divider className="my-4" />
							<Text className="mb-2 text-sm font-medium text-gray-700">
								Or create a new value column
							</Text>
						</>
					)}

					<FormControl isInvalid={!!labelError}>
						<FormLabel className="text-sm font-medium text-gray-700">
							Column Label
						</FormLabel>
						<Input
							value={label}
							onChange={(e) => {
								setLabel(e.target.value);
								if (labelError) {
									validateLabel(e.target.value);
								}
							}}
							placeholder="Enter column label"
							data-testid="column-label-input"
						/>
						{labelError && (
							<FormErrorMessage>{labelError}</FormErrorMessage>
						)}
						{collisionWarning && !labelError && (
							<Text
								className="mt-2 text-xs text-amber-600"
								data-testid="collision-warning"
							>
								{collisionWarning}
							</Text>
						)}
					</FormControl>
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						onClick={onClose}
						className="mr-3"
						data-testid="cancel-add-column"
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						onClick={handleSubmit}
						disabled={isSubmitting}
						data-testid="submit-add-column"
					>
						{isSubmitting ? "Adding..." : "Add Column"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { AddColumnModal };
